import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ClipboardList, Save, Power, Building2, MapPin, Phone, Mail,
  CreditCard, FileText, Plus, Trash2, Download, AlertTriangle,
  CheckCircle2, XCircle, Link2, Shield, Send, Wifi, WifiOff,
  Loader2, Clock, CheckCircle, RefreshCw
} from 'lucide-react';
import { PhoneInput } from '@/components/ui/phone-input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Tarif595Settings = ({ token }) => {
  const [activeTab, setActiveTab] = useState('provider');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Provider settings
  const [settings, setSettings] = useState({
    enabled: false,
    company_name: '',
    gln: '',
    zsr: '',
    street: '',
    house_no: '',
    zip_code: '',
    city: '',
    canton: '',
    phone: '',
    email: '',
    iban: '',
    vat_number: '',
    contact_person: '',
  });

  // Tarif mappings
  const [mappings, setMappings] = useState([]);
  const [tarifCodes, setTarifCodes] = useState([]);
  const [services, setServices] = useState([]);
  const [cantonCodes, setCantonCodes] = useState([]);

  // New mapping form
  const [newMapping, setNewMapping] = useState({ service_id: '', tarif_code: '' });

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});

  // MediData
  const [mediDataSettings, setMediDataSettings] = useState({
    enabled: false,
    sender_id: '',
    recipient_id: '',
    test_mode: true,
    notes: '',
  });
  const [mediDataQueue, setMediDataQueue] = useState([]);
  const [mediDataStats, setMediDataStats] = useState({});
  const [savingMediData, setSavingMediData] = useState(false);
  const [loadingMediData, setLoadingMediData] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [settingsRes, statsRes, cantonRes] = await Promise.all([
        axios.get(`${API}/tarif595/provider-settings`, { headers }),
        axios.get(`${API}/tarif595/stats`, { headers }),
        axios.get(`${API}/tarif595/canton-codes`, { headers }),
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setCantonCodes(cantonRes.data);
    } catch (err) {
      console.error('Fehler beim Laden', err);
    }
    setLoading(false);
  };

  const loadMappings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [mappingsRes, codesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/tarif595/mappings`, { headers }),
        axios.get(`${API}/tarif595/tarif-codes`, { headers }),
        axios.get(`${API}/services`, { headers }),
      ]);
      setMappings(mappingsRes.data);
      setTarifCodes(codesRes.data);
      setServices(servicesRes.data);
    } catch (err) {
      console.error('Fehler beim Laden der Mappings', err);
    }
  };

  const loadInvoices = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/tarif595/invoices`, { headers });
      setInvoices(res.data);
    } catch (err) {
      console.error('Fehler beim Laden der Rechnungen', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'mapping') loadMappings();
    if (activeTab === 'invoices') loadInvoices();
    if (activeTab === 'medidata') loadMediData();
  }, [activeTab]);

  const loadMediData = async () => {
    setLoadingMediData(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [settingsRes, queueRes, statsRes] = await Promise.all([
        axios.get(`${API}/tarif595/medidata/settings`, { headers }),
        axios.get(`${API}/tarif595/medidata/queue`, { headers }),
        axios.get(`${API}/tarif595/medidata/stats`, { headers }),
      ]);
      setMediDataSettings(settingsRes.data);
      setMediDataQueue(queueRes.data);
      setMediDataStats(statsRes.data);
    } catch (err) {
      console.error('MediData load error', err);
    }
    setLoadingMediData(false);
  };

  const saveMediDataSettings = async () => {
    setSavingMediData(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/tarif595/medidata/settings`, mediDataSettings, { headers });
      toast.success('MediData-Einstellungen gespeichert');
      loadMediData();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
    setSavingMediData(false);
  };

  const toggleEnabled = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/tarif595/provider-settings/toggle`, {}, { headers });
      setSettings(prev => ({ ...prev, enabled: res.data.enabled }));
      setStats(prev => ({ ...prev, enabled: res.data.enabled }));
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Fehler beim Umschalten');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const { type, ...data } = settings;
      await axios.put(`${API}/tarif595/provider-settings`, data, { headers });
      toast.success('Einstellungen gespeichert');
      loadData();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const addMapping = async () => {
    if (!newMapping.service_id || !newMapping.tarif_code) {
      toast.error('Bitte Service und Tarif-Code wählen');
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/tarif595/mappings`, newMapping, { headers });
      toast.success('Mapping erstellt');
      setNewMapping({ service_id: '', tarif_code: '' });
      loadMappings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const deleteMapping = async (id) => {
    if (!window.confirm('Mapping wirklich löschen?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/tarif595/mappings/${id}`, { headers });
      toast.success('Mapping gelöscht');
      loadMappings();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const downloadXml = async (invoiceId, invoiceNumber) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/tarif595/invoices/${invoiceId}/xml`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('XML heruntergeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim XML-Download');
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="p-8 text-center text-gray-500">Laden...</CardContent></Card>
    );
  }

  const tabs = [
    { id: 'provider', label: 'Provider', icon: Building2 },
    { id: 'mapping', label: 'Tarif-Mapping', icon: Link2 },
    { id: 'invoices', label: 'Rechnungen', icon: FileText },
    { id: 'medidata', label: 'MediData', icon: Send },
  ];

  return (
    <div className="space-y-6" data-testid="tarif595-settings">
      {/* Header with toggle */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ClipboardList size={28} className="text-teal-600" />
              <div>
                <CardTitle className="text-xl">Tarif 595 - Krankenkassen-Abrechnung</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Schweizer Gesundheitsförderungs-Tarif für Versicherungsbelege (XML 5.0)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {settings.enabled ? 'Aktiviert' : 'Deaktiviert'}
              </span>
              <Switch
                data-testid="tarif595-toggle"
                checked={settings.enabled}
                onCheckedChange={toggleEnabled}
              />
            </div>
          </div>
        </CardHeader>
        {!settings.enabled && (
          <CardContent className="pt-0">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Modul deaktiviert</p>
                <p className="text-sm text-amber-700 mt-1">
                  Aktivieren Sie das Modul, um Tarif 595 Rechnungen zu erstellen. 
                  Sie benötigen eine GLN-Nummer für die Rechnungserstellung.
                </p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Warnung: Platzhalter-Anbieterdaten */}
      {(settings.placeholder_fields || []).length > 0 && (
        <Card className="border-red-300">
          <CardContent className="p-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="tarif595-placeholder-warning">
              <AlertTriangle size={22} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-800">⚠️ Platzhalter-Anbieterdaten hinterlegt — nicht bereit für 2027!</p>
                <p className="text-sm text-red-700 mt-1">
                  Folgende Felder enthalten noch Demo-Werte:{' '}
                  <strong>{(settings.placeholder_fields || []).map(f => ({ gln: 'GLN', zsr: 'ZSR-Nummer', street: 'Strasse', iban: 'IBAN', company_name: 'Firmenname' }[f] || f)).join(', ')}</strong>.
                  Krankenkassen lehnen Belege mit ungültiger GLN/ZSR ab.
                </p>
                <p className="text-sm text-red-700 mt-1">
                  → ZSR-Nummer via <strong>SASIS AG (sasis.ch)</strong> beantragen, GLN via <strong>refdata.ch</strong> — dann unten im Provider-Tab eintragen.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-bold ${stats.has_gln ? 'text-green-600' : 'text-gray-400'}`}>
              {stats.has_gln ? <CheckCircle2 size={28} className="mx-auto" /> : <XCircle size={28} className="mx-auto" />}
            </div>
            <p className="text-xs text-gray-500 mt-1">GLN</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-bold ${stats.has_zsr ? 'text-green-600' : 'text-gray-400'}`}>
              {stats.has_zsr ? <CheckCircle2 size={28} className="mx-auto" /> : <XCircle size={28} className="mx-auto" />}
            </div>
            <p className="text-xs text-gray-500 mt-1">ZSR</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-teal-600">{stats.total_mappings || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Mappings</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-teal-600">{stats.total_invoices || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Rechnungen</p>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`tarif595-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-white text-teal-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Provider Settings Tab */}
      {activeTab === 'provider' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 size={20} className="text-teal-600" />
              Leistungserbringer-Daten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* GLN & ZSR */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <h4 className="font-medium text-teal-800 mb-3 flex items-center gap-2">
                <Shield size={16} />
                Identifikation (Pflichtfelder für Rechnungserstellung)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gln">GLN-Nummer (13-stellig)</Label>
                  <Input
                    id="gln"
                    data-testid="tarif595-gln"
                    placeholder="z.B. 7601234567890"
                    value={settings.gln}
                    onChange={e => setSettings(prev => ({ ...prev, gln: e.target.value }))}
                    maxLength={13}
                  />
                  <p className="text-xs text-gray-500 mt-1">Global Location Number - erhaeltlich bei GS1 Schweiz</p>
                </div>
                <div>
                  <Label htmlFor="zsr">ZSR-Nummer</Label>
                  <Input
                    id="zsr"
                    data-testid="tarif595-zsr"
                    placeholder="z.B. Q123456"
                    value={settings.zsr}
                    onChange={e => setSettings(prev => ({ ...prev, zsr: e.target.value }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">Zahlstellenregister-Nummer - erhaeltlich bei SASIS AG</p>
                </div>
              </div>
            </div>

            {/* Company Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name">Firmenname</Label>
                <Input
                  id="company_name"
                  data-testid="tarif595-company-name"
                  placeholder="StrongFit GmbH"
                  value={settings.company_name}
                  onChange={e => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="contact_person">Kontaktperson</Label>
                <Input
                  id="contact_person"
                  placeholder="Max Muster"
                  value={settings.contact_person}
                  onChange={e => setSettings(prev => ({ ...prev, contact_person: e.target.value }))}
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                <MapPin size={16} />
                Adresse
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="street">Strasse</Label>
                  <Input
                    id="street"
                    placeholder="Musterstrasse"
                    value={settings.street}
                    onChange={e => setSettings(prev => ({ ...prev, street: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="house_no">Hausnr.</Label>
                  <Input
                    id="house_no"
                    placeholder="12a"
                    value={settings.house_no}
                    onChange={e => setSettings(prev => ({ ...prev, house_no: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div>
                  <Label htmlFor="zip_code">PLZ</Label>
                  <Input
                    id="zip_code"
                    placeholder="8000"
                    value={settings.zip_code}
                    onChange={e => setSettings(prev => ({ ...prev, zip_code: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="city">Ort</Label>
                  <Input
                    id="city"
                    placeholder="Zuerich"
                    value={settings.city}
                    onChange={e => setSettings(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="canton">Kanton</Label>
                  <Select
                    value={settings.canton}
                    onValueChange={v => setSettings(prev => ({ ...prev, canton: v }))}
                  >
                    <SelectTrigger data-testid="tarif595-canton">
                      <SelectValue placeholder="Kanton wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {cantonCodes.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Phone size={16} />
                Kontakt
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Telefon</Label>
                  <PhoneInput
                    id="phone"
                    data-testid="tarif595-phone"
                    value={settings.phone}
                    onChange={e => setSettings(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="info@strongfit.ch"
                    value={settings.email}
                    onChange={e => setSettings(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Payment */}
            <div>
              <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                <CreditCard size={16} />
                Zahlungsdaten (für QR-Rechnung)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="iban">IBAN</Label>
                  <Input
                    id="iban"
                    data-testid="tarif595-iban"
                    placeholder="CH93 0076 2011 6238 5295 7"
                    value={settings.iban}
                    onChange={e => setSettings(prev => ({ ...prev, iban: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="vat_number">MWST-Nummer (optional)</Label>
                  <Input
                    id="vat_number"
                    placeholder="CHE-123.456.789"
                    value={settings.vat_number}
                    onChange={e => setSettings(prev => ({ ...prev, vat_number: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <Button
              data-testid="tarif595-save-settings"
              onClick={saveSettings}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Save size={16} className="mr-2" />
              {saving ? 'Speichern...' : 'Einstellungen speichern'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tarif Mapping Tab */}
      {activeTab === 'mapping' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Link2 size={20} className="text-teal-600" />
              Service-Tarif-Zuordnung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Ordnen Sie Ihre StrongFit-Services den offiziellen Tarif 595 Codes zu. 
              Diese Zuordnung wird für die Rechnungserstellung verwendet.
            </p>

            {/* Add new mapping */}
            <div className="bg-gray-50 rounded-lg p-4 border">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Plus size={16} />
                Neue Zuordnung
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select
                  value={newMapping.service_id}
                  onValueChange={v => setNewMapping(prev => ({ ...prev, service_id: v }))}
                >
                  <SelectTrigger data-testid="tarif595-mapping-service">
                    <SelectValue placeholder="Service wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={newMapping.tarif_code}
                  onValueChange={v => setNewMapping(prev => ({ ...prev, tarif_code: v }))}
                >
                  <SelectTrigger data-testid="tarif595-mapping-code">
                    <SelectValue placeholder="Tarif-Code wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tarifCodes.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} - {c.name_de}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  data-testid="tarif595-add-mapping"
                  onClick={addMapping}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Plus size={16} className="mr-2" />
                  Zuordnen
                </Button>
              </div>
            </div>

            {/* Existing mappings */}
            {mappings.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Link2 size={40} className="mx-auto mb-3 opacity-50" />
                <p>Keine Zuordnungen vorhanden</p>
                <p className="text-sm">Erstellen Sie Ihre erste Service-Tarif-Zuordnung</p>
              </div>
            ) : (
              <div className="space-y-2">
                {mappings.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <Badge variant="outline" className="font-mono text-teal-700 border-teal-300">
                        {m.tarif_code}
                      </Badge>
                      <div>
                        <p className="font-medium text-sm">{m.service_name}</p>
                        <p className="text-xs text-gray-500">{m.tarif_name}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMapping(m.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText size={20} className="text-teal-600" />
              Tarif 595 Rechnungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <FileText size={40} className="mx-auto mb-3 opacity-50" />
                <p>Keine Rechnungen vorhanden</p>
                <p className="text-sm">Rechnungen koennen unter Kunden &gt; Versicherungsbelege erstellt werden</p>
              </div>
            ) : (
              <div className="space-y-2">
                {invoices.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-4">
                      <Badge
                        variant={inv.status === 'finalized' ? 'default' : inv.status === 'cancelled' ? 'destructive' : 'secondary'}
                      >
                        {inv.status === 'draft' ? 'Entwurf' : inv.status === 'finalized' ? 'Finalisiert' : 'Storniert'}
                      </Badge>
                      <div>
                        <p className="font-medium text-sm">{inv.invoice_number}</p>
                        <p className="text-xs text-gray-500">
                          {inv.customer_name} - CHF {inv.total_amount?.toFixed(2)}
                          {inv.auto_generated && (
                            <Badge variant="outline" className="ml-2 text-[10px] py-0 border-teal-300 text-teal-600">Auto</Badge>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString('de-CH') : ''}
                      </span>
                      {inv.status !== 'cancelled' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadXml(inv.id, inv.invoice_number)}
                          data-testid={`tarif595-download-xml-${inv.id}`}
                        >
                          <Download size={14} className="mr-1" />
                          XML
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* MediData Tab */}
      {activeTab === 'medidata' && (
        <div className="space-y-4">
          {/* MediData Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Send size={20} className="text-teal-600" />
                eTG / MediData Konfiguration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Konfigurieren Sie die elektronische Übermittlung Ihrer Tarif 595 Rechnungen 
                direkt an die Krankenkassen via eTG/MediData-Schnittstelle.
              </p>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  {mediDataSettings.enabled ? (
                    <Wifi size={20} className="text-green-600" />
                  ) : (
                    <WifiOff size={20} className="text-gray-400" />
                  )}
                  <div>
                    <p className="font-medium text-sm">
                      {mediDataSettings.enabled ? 'MediData aktiviert' : 'MediData deaktiviert'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {mediDataSettings.test_mode ? 'Testmodus aktiv' : 'Produktivmodus'}
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="medidata-toggle"
                  checked={mediDataSettings.enabled}
                  onCheckedChange={v => setMediDataSettings(prev => ({ ...prev, enabled: v }))}
                />
              </div>

              {mediDataSettings.test_mode && mediDataSettings.enabled && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700">
                    Im Testmodus werden Rechnungen simuliert, nicht tatsächlich an die Kasse gesendet.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sender_id">Absender-ID (GLN)</Label>
                  <Input
                    id="sender_id"
                    data-testid="medidata-sender-id"
                    placeholder="GLN des Leistungserbringers"
                    value={mediDataSettings.sender_id}
                    onChange={e => setMediDataSettings(prev => ({ ...prev, sender_id: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="recipient_id">Empfaenger-ID (Clearing-Nr.)</Label>
                  <Input
                    id="recipient_id"
                    data-testid="medidata-recipient-id"
                    placeholder="z.B. MediData Clearing-Nr."
                    value={mediDataSettings.recipient_id}
                    onChange={e => setMediDataSettings(prev => ({ ...prev, recipient_id: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Testmodus</p>
                  <p className="text-xs text-gray-500">Rechnungen werden simuliert, nicht gesendet</p>
                </div>
                <Switch
                  data-testid="medidata-test-mode"
                  checked={mediDataSettings.test_mode}
                  onCheckedChange={v => setMediDataSettings(prev => ({ ...prev, test_mode: v }))}
                />
              </div>

              <div>
                <Label htmlFor="medidata_notes">Notizen</Label>
                <Input
                  id="medidata_notes"
                  placeholder="Interne Notizen..."
                  value={mediDataSettings.notes}
                  onChange={e => setMediDataSettings(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              <Button
                data-testid="medidata-save-settings"
                onClick={saveMediDataSettings}
                disabled={savingMediData}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Save size={16} className="mr-2" />
                {savingMediData ? 'Speichern...' : 'Einstellungen speichern'}
              </Button>
            </CardContent>
          </Card>

          {/* MediData Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Warteschlange', value: mediDataStats.queued || 0, color: 'text-gray-600' },
              { label: 'Gesendet', value: mediDataStats.sent || 0, color: 'text-blue-600' },
              { label: 'Bestätigt', value: mediDataStats.confirmed || 0, color: 'text-green-600' },
              { label: 'Abgelehnt', value: mediDataStats.rejected || 0, color: 'text-red-600' },
              { label: 'Gesamt', value: mediDataStats.total || 0, color: 'text-teal-600' },
            ].map(s => (
              <Card key={s.label}>
                <CardContent className="p-3 text-center">
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* MediData Queue */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock size={18} className="text-teal-600" />
                  Übermittlungs-Protokoll
                </CardTitle>
                <Button
                  variant="outline" size="sm"
                  onClick={loadMediData}
                  disabled={loadingMediData}
                  data-testid="medidata-refresh-queue"
                >
                  <RefreshCw size={14} className={loadingMediData ? 'animate-spin' : ''} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {mediDataQueue.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  <Send size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Noch keine Übermittlungen</p>
                  <p className="text-xs">Finalisierte Rechnungen koennen unter Kunden &gt; T595 an die Kasse gesendet werden</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {mediDataQueue.map(q => (
                    <div key={q.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <Badge
                          variant="outline"
                          className={
                            q.status === 'sent' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                            q.status === 'confirmed' ? 'border-green-300 text-green-700 bg-green-50' :
                            q.status === 'rejected' ? 'border-red-300 text-red-700 bg-red-50' :
                            q.status === 'error' ? 'border-red-300 text-red-700 bg-red-50' :
                            'border-gray-300 text-gray-600'
                          }
                        >
                          {q.status === 'queued' && 'Warteschlange'}
                          {q.status === 'sending' && 'Sendet...'}
                          {q.status === 'sent' && 'Gesendet'}
                          {q.status === 'confirmed' && 'Bestätigt'}
                          {q.status === 'rejected' && 'Abgelehnt'}
                          {q.status === 'error' && 'Fehler'}
                        </Badge>
                        <div>
                          <p className="font-medium text-sm">{q.invoice_number}</p>
                          <p className="text-xs text-gray-500">
                            {q.customer_name} - CHF {q.total_amount?.toFixed(2)}
                            {q.test_mode && <span className="ml-1 text-amber-600">(Test)</span>}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">
                          {q.created_at ? new Date(q.created_at).toLocaleDateString('de-CH') : ''}
                        </p>
                        {q.response_message && (
                          <p className="text-xs text-gray-500 mt-0.5">{q.response_message}</p>
                        )}
                        {q.error_message && (
                          <p className="text-xs text-red-500 mt-0.5">{q.error_message}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Tarif595Settings;
